import React, { useCallback, useMemo, useState } from 'react';
import noop from 'lodash/noop';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type {
	DraggableProvided,
	DraggableStateSnapshot,
	DraggableRubric,
} from 'react-beautiful-dnd';
import { GROUP_BY_STATUS } from '@atlassian/jira-business-constants';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { useSimpleSearch } from '@atlassian/jira-business-filters';
import { useCanCreateIssueInStatus } from '@atlassian/jira-business-issue-create';
import { Column } from '@atlassian/jira-platform-board-kit-legacy-dnd';
import { NULL_GROUP_KEY } from '../../../common/constants';
import type { BoardColumn as BoardColumnType, BoardIssueOld } from '../../../common/types';
import { useIsColumnDroppableOld } from '../../../controllers/column-droppable-old';
import { useGroupBy } from '../../../controllers/group-by';
import {
	useBoardStore,
	useHasMultipleTransitionsBetweenStatuses,
	useActiveWorkflowDetails,
} from '../../../services/board-store';
import CardActionsMenu from './card-actions';
import JWMCard from './card-old';
import ColumnHeaderWithPivotSupport from './column-header-with-pivot-support-old';
import InlineCreate from './inline-create';
import { CardsWrapper } from './styled';
import { TransitionZones } from './transition-zones';
import VirtualCards from './virtual-cards-old';

type Props = {
	isClone?: boolean;
	isVirtualized?: boolean;
	columnIndex: number;
	column: BoardColumnType;
	boardSize?: {
		width: number;
		height: number;
	};
	scrollRef?: {
		current: HTMLElement | null;
	};
	isDragging?: boolean;
};

type CardMenuProps = {
	onContentResize: () => void;
	onClosePopup: () => void;
	onOpenPopup: () => void;
};

const BoardColumn = ({
	isVirtualized,
	column,
	scrollRef,
	boardSize,
	columnIndex,
	isClone,
	isDragging,
}: Props) => {
	const isDroppable = useIsColumnDroppableOld(Number(column.statusId));
	const [simpleSearch] = useSimpleSearch();
	const [{ columnToDelete, columnToDeleteHover }] = useBoardStore();
	const [hasMultipleTransitions] = useHasMultipleTransitionsBetweenStatuses();
	const checkCanCreateIssueInStatus = useCanCreateIssueInStatus();
	const [siblingCreateIssueId, setSiblingCreateIssueId] = useState<number | null>(null);
	const [activeWorkflowDetails] = useActiveWorkflowDetails();
	const [groupBy] = useGroupBy();
	const isGroupedByStatus = groupBy === GROUP_BY_STATUS;

	const project = useProject();
	const canAdministerProject = project.permissions.administerProject;

	const isCollapsedColumn = !!column.isCollapsed;
	const isDeletableColumn = useMemo(
		() =>
			columnToDelete?.statusId === column.statusId ||
			columnToDeleteHover?.statusId === column.statusId,
		[columnToDelete, column, columnToDeleteHover],
	);

	const textHighlight = useMemo(
		() => (simpleSearch != null ? [simpleSearch] : undefined),
		[simpleSearch],
	);

	const canCreateIssues = useMemo(() => {
		if (!project.permissions.createIssues) {
			return false;
		}

		if (
			isGroupedByStatus &&
			!checkCanCreateIssueInStatus(String(column.statusId), activeWorkflowDetails)
		) {
			return false;
		}

		return true;
	}, [activeWorkflowDetails, checkCanCreateIssueInStatus, column, isGroupedByStatus, project]);

	const cardRenderer = useCallback(
		({ issue, cardIndex }: { issue: BoardIssueOld; cardIndex: number }) => (
			<JWMCard
				key={issue.id}
				draggableIndex={cardIndex}
				canCreateIssues={canCreateIssues}
				column={column}
				issue={issue}
				cardMenu={({ onContentResize, onClosePopup, onOpenPopup }: CardMenuProps) => (
					<CardActionsMenu
						issue={issue}
						firstIssueId={column.issues[0].id}
						lastIssueId={column.issues[column.issues.length - 1].id}
						onContentResize={onContentResize}
						onClosePopup={onClosePopup}
						onOpenPopup={onOpenPopup}
					/>
				)}
				highlight={textHighlight}
				isSiblingCreateFormOpen={siblingCreateIssueId === issue.id}
				toggleSiblingCreateForm={() => {
					setSiblingCreateIssueId(siblingCreateIssueId === issue.id ? null : issue.id);
				}}
			/>
		),
		[canCreateIssues, column, textHighlight, siblingCreateIssueId],
	);

	const renderDraggableCard = useCallback(
		(provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: DraggableRubric) => {
			const issue = column.issues[rubric.source.index];
			if (!issue) {
				return null;
			}

			return (
				<div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
					<JWMCard
						isDragging={snapshot.isDragging}
						key={issue.id}
						isClone
						draggableIndex={rubric.source.index}
						issue={issue}
						canCreateIssues={canCreateIssues}
						cardMenu={null}
						column={column}
						isSiblingCreateFormOpen={false}
						toggleSiblingCreateForm={noop}
					/>
				</div>
			);
		},
		[canCreateIssues, column],
	);

	const isDropDisabled = useMemo(
		() =>
			(!isDroppable && column.groupKey !== NULL_GROUP_KEY) ||
			(isCollapsedColumn && hasMultipleTransitions && isGroupedByStatus),
		[column.groupKey, hasMultipleTransitions, isCollapsedColumn, isDroppable, isGroupedByStatus],
	);

	const columnFooter = useMemo(
		() => (canCreateIssues ? <InlineCreate column={column} /> : null),
		[canCreateIssues, column],
	);

	return useMemo(
		() => (
			<Column
				id={column.id}
				isDraggable={canAdministerProject && !isClone}
				isDragging={isDragging && !isCollapsedColumn} // Prevent dragging shadow for collapsed columns
				isDropDisabled={isDropDisabled}
				isDone={column.isDone}
				isUpdating={column?.loading ?? false}
				header={
					<ColumnHeaderWithPivotSupport
						column={column}
						isClone={isClone}
						isDeletableColumn={isDeletableColumn}
						isDropDisabled={!isDroppable}
					/>
				}
				draggableIndex={columnIndex}
				TransitionZones={TransitionZones}
				footer={columnFooter}
				dropZoneMode="virtual"
				// @ts-expect-error - TS2322 - Type '(provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: DraggableRubric) => JSX.Element | null' is not assignable to type 'DraggableChildrenFn'.
				renderClone={renderDraggableCard}
				isCollapsed={isCollapsedColumn}
			>
				{/* @ts-expect-error - TS7006 - Parameter 'dropProvided' implicitly has an 'any' type. | TS7006 - Parameter 'snapshot' implicitly has an 'any' type. | TS7006 - Parameter 'columnDroppableProps' implicitly has an 'any' type. */}
				{(dropProvided, snapshot, columnDroppableProps) => (
					// This render function will later be an entry point for virtualisation

					<CardsWrapper
						isDeletableColumn={isDeletableColumn}
						ref={dropProvided.innerRef}
						{...dropProvided.droppableProps}
						isDisabled={columnDroppableProps.isDropDisabled}
						isCollapsed={isCollapsedColumn}
					>
						{scrollRef && isVirtualized !== true && (
							<VirtualCards
								scrollRef={scrollRef}
								cardRenderer={cardRenderer}
								issues={column.issues}
								boardSize={boardSize}
								footer={columnFooter}
								dndSnapshot={snapshot}
								isClone={isClone}
								isCollapsedColumn={isCollapsedColumn}
							/>
						)}
					</CardsWrapper>
				)}
			</Column>
		),
		[
			canAdministerProject,
			column,
			isClone,
			isDropDisabled,
			isDeletableColumn,
			isDroppable,
			columnIndex,
			columnFooter,
			renderDraggableCard,
			isCollapsedColumn,
			scrollRef,
			isVirtualized,
			cardRenderer,
			boardSize,
			isDragging,
		],
	);
};
export default BoardColumn;
